import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/pages/marketing/components/Header";
import Footer from "@/pages/marketing/components/Footer";
import PageSEO from "@/components/seo/PageSEO";
import StructuredData from "@/components/seo/StructuredData";

// FAQ data organized by category
const sellerFaqs = {
  gettingStarted: [
    {
      question: "Is GoDyspo the same as GoDispo, Go Dyspo, or Go Dispo?",
      answer: "Yes — these are all common spellings of the same brand. The official name is GoDyspo and the only official website is https://godyspo.com. Any similarly named domain is not affiliated with us."
    },
    {
      question: "What is GoDyspo?",
      answer: "GoDyspo (also spelled GoDispo) is a real estate disposition platform designed for wholesalers and agents to list investment properties and connect with qualified buyers. Our platform streamlines the process of marketing deals, managing buyer lists, and closing transactions faster."
    },
    {
      question: "How do I create a seller account?",
      answer: "Creating an account is simple. Click 'Start Free Trial' on our homepage, enter your business information, verify your email address, and you're ready to start listing properties. The entire process takes less than 5 minutes."
    },
    {
      question: "Is there a free trial?",
      answer: "Yes! We offer a 30-day free trial with full access to all platform features. No credit card is required to start your trial. You can list properties, manage buyers, and explore all tools before deciding to subscribe."
    },
    {
      question: "What can I do during the free trial?",
      answer: "During your 30-day free trial, you have complete access to all features: list unlimited properties, create buyer lists, receive and manage offers, access analytics, and use all communication tools. There are no feature restrictions during the trial period."
    }
  ],
  propertyManagement: [
    {
      question: "How do I list a property?",
      answer: "From your dashboard, click 'Add Property' and fill in the property details including address, price, ARV, bedrooms, bathrooms, and photos. You can also add videos, property descriptions, and set contract expiration dates. Your listing goes live immediately after saving."
    },
    {
      question: "What information do I need to list a property?",
      answer: "At minimum, you need the property address, asking price, number of bedrooms and bathrooms, and square footage. We recommend also including the ARV (After Repair Value), property photos, a detailed description, and any relevant financing options to attract more buyers."
    },
    {
      question: "Can I upload photos and videos?",
      answer: "Absolutely! You can upload multiple high-quality photos and videos for each property. Photos appear in an interactive gallery, and videos help buyers get a better feel for the property before scheduling a viewing."
    },
    {
      question: "How do I edit or update my listings?",
      answer: "Navigate to your property from the dashboard or Active Deals page, then click the 'Edit' button. You can update any property information, add or remove photos, change the price, or update the status at any time."
    },
    {
      question: "What happens when a property sells?",
      answer: "When you close a deal, mark the property as 'Closed' in the system. You can record the sale price and closing date for your records. Closed properties move to your Closed Deals section for historical tracking and analytics."
    }
  ],
  subscriptionBilling: [
    {
      question: "How much does GoDyspo cost?",
      answer: "GoDyspo offers flexible subscription plans starting at $29.99/month. We offer monthly and annual billing options, with significant savings on annual plans. Visit our Pricing page for detailed plan comparisons and features."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards including Visa, Mastercard, American Express, and Discover. Payments are processed securely through Stripe, ensuring your financial information is always protected."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription at any time with no cancellation fees. Your access continues until the end of your current billing period. You can reactivate your subscription whenever you're ready to return."
    },
    {
      question: "What happens if my subscription expires?",
      answer: "If your subscription expires, your listings remain saved but become inactive and won't be visible to buyers. You'll retain access to view your data, but won't be able to add new properties or communicate with buyers until you resubscribe."
    }
  ],
  buyerManagement: [
    {
      question: "How do I find buyers for my deals?",
      answer: "GoDyspo connects you with qualified real estate investors actively searching for deals. Buyers can find your properties through our marketplace search, and you can also build and manage your own buyer lists to market directly to your network."
    },
    {
      question: "Can I create buyer lists?",
      answer: "Yes! You can create unlimited buyer lists to organize your contacts. Group buyers by market, investment criteria, or any category that fits your business. Easily add buyers from the platform's investor network or import your existing contacts."
    },
    {
      question: "How do buyers find my properties?",
      answer: "Your active listings appear in our buyer marketplace where investors can search by location, price, property type, and other criteria. Properties matching buyer preferences are highlighted, increasing visibility to the most relevant investors."
    },
    {
      question: "How do I contact interested buyers?",
      answer: "When buyers express interest or submit offers on your properties, you receive instant notifications. You can view buyer profiles, see their investment preferences, and communicate directly through the platform or via their provided contact information."
    }
  ],
  offersNegotiations: [
    {
      question: "How do I receive offers on my properties?",
      answer: "Buyers can submit offers directly through your property listings. You'll receive instant notifications via email and in-app alerts when new offers come in. All offers are organized in your Offers dashboard for easy management."
    },
    {
      question: "Can I accept, reject, or counter offers?",
      answer: "Yes, you have full control over offer management. Review each offer's details including price, proposed closing date, and buyer notes. Accept offers you're happy with, reject those that don't meet your criteria, or respond with a counter-offer to negotiate."
    },
    {
      question: "How are buyers notified about my decision?",
      answer: "When you respond to an offer, the buyer receives an instant notification with your decision. If you accept, they get next steps for closing. If you counter, they can review your terms and respond. All communication is tracked in the platform."
    }
  ]
};

// Flatten FAQs for structured data
const allFaqs = [
  ...sellerFaqs.gettingStarted,
  ...sellerFaqs.propertyManagement,
  ...sellerFaqs.subscriptionBilling,
  ...sellerFaqs.buyerManagement,
  ...sellerFaqs.offersNegotiations
];

const SellerFAQPage = () => {
  return (
    <div className="min-h-screen bg-godyspo-background flex flex-col">
      <PageSEO
        title="Seller FAQ | Real Estate Disposition Questions"
        description="Get answers to common questions about listing properties, managing buyers, subscription pricing, and using GoDyspo's real estate disposition platform."
        canonical="/seller/faq"
        keywords="real estate disposition FAQ, wholesaler questions, property listing help, GoDyspo seller support"
      />
      <StructuredData type="FAQPage" faqs={allFaqs} />
      
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Seller <span className="text-godyspo-gold">FAQ</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Find answers to common questions about listing properties and growing your real estate business on GoDyspo.
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="max-w-3xl mx-auto space-y-8">
          {/* Getting Started */}
          <section>
            <h2 className="text-2xl font-semibold text-godyspo-gold mb-4">Getting Started</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {sellerFaqs.gettingStarted.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`getting-started-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-godyspo-gold text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Property Management */}
          <section>
            <h2 className="text-2xl font-semibold text-godyspo-gold mb-4">Property Management</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {sellerFaqs.propertyManagement.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`property-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-godyspo-gold text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Subscription & Billing */}
          <section>
            <h2 className="text-2xl font-semibold text-godyspo-gold mb-4">Subscription & Billing</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {sellerFaqs.subscriptionBilling.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`billing-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-godyspo-gold text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Buyer Management */}
          <section>
            <h2 className="text-2xl font-semibold text-godyspo-gold mb-4">Buyer Management</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {sellerFaqs.buyerManagement.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`buyers-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-godyspo-gold text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Offers & Negotiations */}
          <section>
            <h2 className="text-2xl font-semibold text-godyspo-gold mb-4">Offers & Negotiations</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {sellerFaqs.offersNegotiations.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`offers-${index}`}
                  className="bg-gray-900/50 border border-gray-800 rounded-lg px-4"
                >
                  <AccordionTrigger className="text-white hover:text-godyspo-gold text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-godyspo-gold/10 to-godyspo-blue/10 rounded-2xl p-8 md:p-12 border border-gray-800">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Listing Properties?
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Join thousands of wholesalers and agents using GoDyspo to close more deals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/seller/register">
              <Button className="bg-godyspo-gold text-godyspo-background hover:bg-godyspo-gold/90 px-8 py-3 text-lg">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800 px-8 py-3 text-lg">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>

        {/* Still Have Questions */}
        <div className="mt-12 text-center">
          <p className="text-gray-400">
            Still have questions?{" "}
            <Link to="/contact" className="text-godyspo-gold hover:underline">
              Contact our support team
            </Link>
          </p>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SellerFAQPage;
